// JSON control messages (§20.2) and their validation. Frozen after WP0: additive changes only.
import { validateCosmetics, type Cosmetics } from '../cosmetics';
import { ARENAS, Difficulty, ModeId, type AwardId, type MapId, type MutatorId, type Personality, type RoomPhase } from '../ids';
import type { RoundConfig, RoundStats } from '../types';

export type ClientMsg =
  | { t: 'hello'; v: number; name: string; profileId: string; cosmetics: Cosmetics; reconnect?: { code: string; token: string } }
  | { t: 'quickPlay' }
  | { t: 'createRoom'; settings: Partial<RoomSettings>; debug?: RoomDebug }
  | { t: 'joinRoom'; code: string }
  | { t: 'leaveRoom' }
  | { t: 'ready'; ready: boolean }
  | { t: 'cosmetics'; cosmetics: Cosmetics }
  | { t: 'settings'; settings: Partial<RoomSettings> }    // host only, LOBBY only
  | { t: 'start' }                                         // host only, LOBBY only
  | { t: 'kick'; slot: number }                            // host only
  | { t: 'vote'; card: 0 | 1 | 2 }
  | { t: 'underdog'; choice: number }                      // index into offered mutators, -1 = no thanks
  | { t: 'skip' }                                          // skip replay / awards
  | { t: 'rematch' }
  | { t: 'ping'; c: number };

export type ServerMsg =
  | { t: 'welcome'; v: number }
  | { t: 'joined'; code: string; slot: number; spectator: boolean; token: string; isHost: boolean }
  | { t: 'room'; room: RoomStateDTO }                      // sent on change, max 10/s per room (coalesced)
  | { t: 'round'; init: RoundInitDTO }                     // new world (lobby, each round, podium) or on join
  | { t: 'roundEnd'; end: RoundEndDTO }
  | { t: 'matchEnd'; end: MatchEndDTO }
  | { t: 'pong'; c: number; tick: number; frac: number }
  | { t: 'toast'; text: string; kind: 'info' | 'join' | 'leave' | 'host' | 'takeover' }
  | { t: 'error'; code: ErrorCode; msg: string }
  | { t: 'kicked'; reason: 'host' | 'afk' | 'abuse' };

export type ErrorCode = 'VERSION_MISMATCH' | 'ROOM_NOT_FOUND' | 'ROOM_FULL' | 'RATE_LIMITED' | 'NOT_HOST' | 'BAD_REQUEST' | 'SERVER_FULL';

export interface RoomSettings {
  playlist: 'party' | 'royale' | 'potato' | 'crown';
  map: 'rotation' | MapId;
  rounds: 3 | 5 | 8;
  golden: boolean;
  items: boolean;
  botFill: boolean;          // fill to 8
  botCount: number;          // 0..7 when botFill = false
  botDifficulty: Difficulty;
  teams: 'off' | 'split' | 'humansVsBots';
  underdog: boolean;
}
export interface RoomDebug { code?: string; seed?: number; roundSec?: number; firstCard?: { mode: ModeId; map: MapId }; goldenFirst?: boolean; startNow?: boolean }

export interface PlayerDTO {
  slot: number; name: string; isBot: boolean; personality: Personality | -1; difficulty: Difficulty;
  identity: number; team: number; cosmetics: Cosmetics; voicePitch: number;
  ready: boolean; connected: boolean; afk: boolean; isHost: boolean; score: number; matchKos: number; ping: number;
}
export interface RoundCard { mode: ModeId; map: MapId }
export interface RoomStateDTO {
  code: string; kind: 'quick' | 'private'; phase: RoomPhase; phaseEndTick: number; roomTick: number;
  hostSlot: number; settings: RoomSettings;
  players: (PlayerDTO | null)[];                 // length 8
  spectators: { name: string }[];
  roundIndex: number; totalRounds: number;      // roundIndex 0-based; golden is last
  cards: RoundCard[]; votes: number[];          // votes per card
  underdog: { slot: number; options: MutatorId[]; chosen: MutatorId | -1; endTick?: number } | null;   // endTick (additive, WP3): room tick the pick closes
  nextMutator: MutatorId;
  lobbyCountdownEndTick: number;                // -1 if none
  rematch: number[];                            // slots that pressed rematch
  /** Additive (WP3): each slot's current vote (card index, −1 none), length 8. */
  voters?: number[];
  /** Additive (WP3): slots that pressed Skip in this INTERMISSION (replay) or AWARDS. */
  skip?: number[];
}
export interface RoundInitDTO {
  roundId: number; roundIndex: number; cfg: RoundConfig;   // full RoundConfig (seed included)
  startRoomTick: number;          // room tick at which round tick = 0
}
export interface RoundEndDTO {
  roundId: number;
  results: { slot: number; place: number; points: number; breakdown: { label: string; pts: number }[] }[];
  winner: number; winningTeam: number;
  highlight: { startTick: number; endTick: number; focusSlot: number; killerSlot: number } | null;   // round ticks
  standings: { slot: number; score: number }[];
}
export interface MatchEndDTO {
  standings: { slot: number; score: number; place: number }[];
  awards: { id: AwardId; slot: number; value: number }[];
  playOfMatch: { roundId: number; startTick: number; endTick: number; focusSlot: number; killerSlot: number } | null;
  stats: RoundStats[];            // match totals per slot (for client challenges/XP and Bean Card)
}

// ---- Defaults and room codes ------------------------------------------------------------------

/** Private-room defaults (§9.4, bold values); Quick Play uses the same values. */
export const DEFAULT_ROOM_SETTINGS: Readonly<RoomSettings> = {
  playlist: 'party', map: 'rotation', rounds: 5, golden: true, items: true,
  botFill: true, botCount: 3, botDifficulty: Difficulty.NORMAL, teams: 'off', underdog: true,
};

export const ROOM_CODE_ALPHABET = 'BCDFGHJKLMNPQRSTVWXZ';
export const ROOM_CODE_LENGTH = 4;

/** Uppercases and validates a room code (case-insensitive, §21.3); null if not a valid code. */
export function normalizeRoomCode(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;
  const code = raw.trim().toUpperCase();
  if (code.length !== ROOM_CODE_LENGTH) return null;
  for (const ch of code) if (!ROOM_CODE_ALPHABET.includes(ch)) return null;
  return code;
}

// ---- Validation of untrusted JSON -------------------------------------------------------------

type Obj = Record<string, unknown>;

const MAX_NAME_INPUT = 64;
const MAX_ID_INPUT = 64;
const MAX_CODE_INPUT = 16;
const ROUND_SEC_MIN = 5;
const ROUND_SEC_MAX = 3600;
const PLAYLISTS: readonly RoomSettings['playlist'][] = ['party', 'royale', 'potato', 'crown'];
const TEAM_MODES: readonly RoomSettings['teams'][] = ['off', 'split', 'humansVsBots'];
const ROUND_COUNTS: readonly RoomSettings['rounds'][] = [3, 5, 8];
const SCORING_MODES: readonly ModeId[] = [ModeId.ROYALE, ModeId.HOT_POTATO, ModeId.CROWN_GRAB];
const DIFFICULTIES: readonly Difficulty[] = [Difficulty.CHILL, Difficulty.NORMAL, Difficulty.SPICY];

function isObj(v: unknown): v is Obj {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function isInt(v: unknown, lo: number, hi: number): v is number {
  return typeof v === 'number' && Number.isInteger(v) && v >= lo && v <= hi;
}

function isStr(v: unknown, maxLen: number): v is string {
  return typeof v === 'string' && v.length <= maxLen;
}

function oneOf<T>(list: readonly T[], v: unknown): v is T {
  return list.includes(v as T);
}

/** Keeps only the known settings fields whose values are valid (§21.7: field-by-field). */
export function sanitizeRoomSettings(raw: unknown): Partial<RoomSettings> {
  const out: Partial<RoomSettings> = {};
  if (!isObj(raw)) return out;
  if (oneOf(PLAYLISTS, raw.playlist)) out.playlist = raw.playlist;
  if (raw.map === 'rotation' || oneOf(ARENAS, raw.map)) out.map = raw.map;
  if (oneOf(ROUND_COUNTS, raw.rounds)) out.rounds = raw.rounds;
  if (typeof raw.golden === 'boolean') out.golden = raw.golden;
  if (typeof raw.items === 'boolean') out.items = raw.items;
  if (typeof raw.botFill === 'boolean') out.botFill = raw.botFill;
  if (isInt(raw.botCount, 0, 7)) out.botCount = raw.botCount;
  if (oneOf(DIFFICULTIES, raw.botDifficulty)) out.botDifficulty = raw.botDifficulty;
  if (oneOf(TEAM_MODES, raw.teams)) out.teams = raw.teams;
  if (typeof raw.underdog === 'boolean') out.underdog = raw.underdog;
  return out;
}

/** Keeps only valid debug fields; `roundSec` is clamped to [5, 3600]. */
export function sanitizeRoomDebug(raw: unknown): RoomDebug {
  const out: RoomDebug = {};
  if (!isObj(raw)) return out;
  const code = normalizeRoomCode(raw.code);
  if (code) out.code = code;
  if (typeof raw.seed === 'number' && Number.isFinite(raw.seed)) out.seed = raw.seed >>> 0;
  if (typeof raw.roundSec === 'number' && Number.isFinite(raw.roundSec) && raw.roundSec > 0) {
    out.roundSec = Math.min(ROUND_SEC_MAX, Math.max(ROUND_SEC_MIN, raw.roundSec));
  }
  const card = raw.firstCard;
  if (isObj(card) && oneOf(SCORING_MODES, card.mode) && oneOf(ARENAS, card.map)) out.firstCard = { mode: card.mode, map: card.map };
  if (typeof raw.goldenFirst === 'boolean') out.goldenFirst = raw.goldenFirst;
  if (typeof raw.startNow === 'boolean') out.startNow = raw.startNow;
  return out;
}

function parseJsonObject(text: string): Obj | null {
  try {
    const v: unknown = JSON.parse(text);
    return isObj(v) && typeof v.t === 'string' ? v : null;
  } catch {
    return null;
  }
}

/**
 * Parses a client text frame into a structurally valid ClientMsg, or null (ignored by the server
 * and counted as malformed). Cosmetics are clamped with `validateCosmetics`, settings and debug
 * fields are filtered; semantic checks (version, host rights, name filter) stay with the server.
 */
export function parseClientMsg(text: string): ClientMsg | null {
  const m = parseJsonObject(text);
  if (!m) return null;
  switch (m.t) {
    case 'hello': {
      if (typeof m.v !== 'number' || !isStr(m.name, MAX_NAME_INPUT) || !isStr(m.profileId, MAX_ID_INPUT)) return null;
      const hello: ClientMsg = { t: 'hello', v: m.v, name: m.name, profileId: m.profileId, cosmetics: validateCosmetics(m.cosmetics) };
      if (m.reconnect !== undefined) {
        const rc = m.reconnect;
        if (!isObj(rc) || !isStr(rc.code, MAX_CODE_INPUT) || !isStr(rc.token, MAX_ID_INPUT)) return null;
        hello.reconnect = { code: rc.code, token: rc.token };
      }
      return hello;
    }
    case 'quickPlay': return { t: 'quickPlay' };
    case 'leaveRoom': return { t: 'leaveRoom' };
    case 'start': return { t: 'start' };
    case 'skip': return { t: 'skip' };
    case 'rematch': return { t: 'rematch' };
    case 'createRoom': {
      const create: ClientMsg = { t: 'createRoom', settings: sanitizeRoomSettings(m.settings) };
      if (m.debug !== undefined) create.debug = sanitizeRoomDebug(m.debug);
      return create;
    }
    case 'joinRoom': return isStr(m.code, MAX_CODE_INPUT) ? { t: 'joinRoom', code: m.code } : null;
    case 'ready': return typeof m.ready === 'boolean' ? { t: 'ready', ready: m.ready } : null;
    case 'cosmetics': return { t: 'cosmetics', cosmetics: validateCosmetics(m.cosmetics) };
    case 'settings': return { t: 'settings', settings: sanitizeRoomSettings(m.settings) };
    case 'kick': return isInt(m.slot, 0, 7) ? { t: 'kick', slot: m.slot } : null;
    case 'vote': return m.card === 0 || m.card === 1 || m.card === 2 ? { t: 'vote', card: m.card } : null;
    case 'underdog': return isInt(m.choice, -1, 255) ? { t: 'underdog', choice: m.choice } : null;
    case 'ping': return typeof m.c === 'number' && Number.isFinite(m.c) ? { t: 'ping', c: m.c } : null;
    default: return null;
  }
}

const SERVER_MSG_TYPES: readonly ServerMsg['t'][] = ['welcome', 'joined', 'room', 'round', 'roundEnd', 'matchEnd', 'pong', 'toast', 'error', 'kicked'];

/** Parses a server text frame. The server is trusted: only the envelope (`t`) is checked. */
export function parseServerMsg(text: string): ServerMsg | null {
  const m = parseJsonObject(text);
  return m && oneOf(SERVER_MSG_TYPES, m.t) ? (m as unknown as ServerMsg) : null;
}
